import { Injectable } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { filter } from "rxjs";
import { UserSecurityGroup } from "src/app/features/security-group/interfaces/security-group.interface";
import { Features } from "../enums/features.enum";
import { StorageService } from "../services/storage.service";
import { FeatureAccess } from "../enums/security-feature/access-level.enum";
import { SecurityFeaturePermission } from "src/app/features/security-group/interfaces/security-feature-permission.interface";

export enum SecurityFeature {
  CORPORATION = "corporation",
}

/**
 * Permissions of the facility pages (About actions and facility tabs). While a facility is open from the Facilities
 * list these follow that facility's role; every other permission keeps following the facility selected in the header.
 */
const FACILITY_PAGE_FEATURES: ReadonlySet<string> = new Set<string>([
  Features.FACILITY_EDIT,
  Features.FACILITY_ENABLE,
  Features.FACILITY_DISABLE,
  Features.FACILITY_DELETE,
  Features.FACILITIES_PAYORS,
  Features.FACILITIES_CONTRACTS,
  Features.FACILITIES_AUTO_FORECAST,
  Features.FACILITIES_PHYSICIANS,
  Features.FACILITIES_REFERRALS,
  Features.FACILITIES_CONTACTS,
  Features.FACILITIES_VENDORS_INTEGRATIONS,
  Features.FACILITIES_VENDORS_SPECIALTY_PROGRAM,
]);

interface FacilityPermissionScope {
  facilityId: string;
  features: UserSecurityGroup[];
}

@Injectable({ providedIn: "root" })
export class SecurityFeatureUtility {
  private facilityScope: FacilityPermissionScope | null = null;

  constructor(private storageService: StorageService, private router: Router) {
    // The scope only applies on that facility's pages; leaving them drops it.
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        if (this.facilityScope && !event.urlAfterRedirects.startsWith(`/facility/${this.facilityScope.facilityId}`)) {
          this.facilityScope = null;
        }
      });
  }

  /** Facility pages apply the permissions of the facility being viewed (see FACILITY_PAGE_FEATURES). */
  setFacilityScope(facilityId: string, features: UserSecurityGroup[]): void {
    this.facilityScope = { facilityId, features };
  }

  clearFacilityScope(): void {
    this.facilityScope = null;
  }

  getSecurityFeature(securityFeature: string, validateAccess: boolean = true) {
    const source =
      this.facilityScope && FACILITY_PAGE_FEATURES.has(securityFeature)
        ? this.facilityScope.features
        : this.storageService.getSecurityFeatureData();
    const feature = source?.find(
      (c: any) => c.frontendidentifier === securityFeature
    ) as UserSecurityGroup;

    if (validateAccess) {
      this.validateAccess(feature);
    }
    ``;

    return feature;
  }

  getPermissions(
    featureIdentifier: Features,
    validateAccess: boolean = true
  ): SecurityFeaturePermission {
    const feature = this.getSecurityFeature(featureIdentifier, validateAccess);
    const canManage = feature?.featureaccess === FeatureAccess.Manage;
    return {
      canView: canManage || feature?.featureaccess === FeatureAccess.View,
      canManage,
    };
  }

  getPermissionsForChildFromParent(
    parentSecurity: Features,
    childSecurity: Features,
    validateAccess: boolean = true
  ) {
    const feature = this.getSecurityFeature(parentSecurity, validateAccess);
    const childFeature = this.getSecurityFeature(childSecurity, validateAccess);
    const canManage =
      childFeature?.featureaccess === FeatureAccess.Manage &&
      (feature?.featureaccess === FeatureAccess.Manage || feature?.featureaccess === FeatureAccess.View);
    return {
      canView:
        canManage ||
        (childFeature?.featureaccess === FeatureAccess.View && feature?.featureaccess === FeatureAccess.View),
      canManage,
    };
  }

  validateAccess(feature: UserSecurityGroup) {
    if (feature?.featureaccess === FeatureAccess.None) {
      this.router.navigate(['/', 'access-denied']);
    }
  }

  linkAccessible(feature: string) {
    return this.getSecurityFeature(feature, false)?.featureaccess !== FeatureAccess.None;
  }

  canManage(feature: UserSecurityGroup): boolean {
    return feature?.featureaccess === FeatureAccess.Manage;
  }

  canView(feature: UserSecurityGroup): boolean {
    return feature?.featureaccess === FeatureAccess.View;
  }

  canViewFeature(featureIdentifier: Features): boolean {
    const feature = this.getSecurityFeature(featureIdentifier, false);
    return (feature?.featureaccess === FeatureAccess.View || feature?.featureaccess === FeatureAccess.Manage);
  }

  canManageFeature(featureIdentifier: Features): boolean {
    const feature = this.getSecurityFeature(featureIdentifier, false);
    return feature?.featureaccess === FeatureAccess.Manage;
  }
}
